import {
    AfterViewInit, ChangeDetectionStrategy,
    Component,
    ElementRef, EventEmitter, HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {BaseFieldComponent} from '../base-field.component';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {SelectValueInterface} from '@interfaces/select-value.interface';
import {FormHelperService} from '@services/form-helper.service';
declare const $: any;

/**
 * Компонент-обертка для Select2.
 *
 * Селект2 по своей сути представляет собой графический виджет, построенный рядом с нативным селектом и управляющий
 * его данными, чтобы в форму уходило нужное значение.
 * Angular в свою очередь через реактивную форму тоже управляет нативным селектом, реагируя на изменения его значений
 * и обновляя его вид под значение поля.
 * Основная суть проблемы и решение ее в виде этого компонента: научить select2 влиять на реактивную форму Angular через
 * нативный селект и наоборот, научить select2 реагировать на изменения реактивной формы, чтобы они друг друга понимали.
 */


@Component({
    selector: 'app-form-select',
    templateUrl: 'form-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
        :host {
            display: block;
        }
    `]
})

export class FormSelectComponent extends BaseFieldComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

    @Input() values: SelectValueInterface[];
    @Input() placeholder = 'Выберите';
    @Input() isSearch = false;

    /**
     * Выставить True, если нужно, чтобы во время загрузки данных
     * селект был задизейблин и отображался плейсхолдер "Загрузка данных..."
     * Селект будет считать себя в процессе загрузки, если values равно null или undefined,
     * поэтому очень важно, чтобы до загрузки значения передавались как null или undefined,
     * а после получения данных или после ошибки получения становились коллекцией (хотя бы пустой [])
     */
    @Input() showLoading = false;

    /**
     * Выбирать доступное значение, если оно всего одно
     */
    @Input() selectIfSingleValue = false;

    /**
     * Проверять существование текущего значения среди доступных и очищать значение, если его нет в доступных?
     */
    @Input() checkValueExistence = false;

    /**
     * Ширина выпадающего списка подстраивается под контент?.
     * false - ширина dropdown будет равна ширине селекта
     * true - ширина dropdown может быть шире селекта, если option содержит много данных
     * @type {boolean}
     */
    @Input() dropdownAutoWidth = false;

    /**
     * Проброс нативного события change.
     * Иногда сложно отследить изменение значения в селекте, чтобы добавить обработчик (например, если селект внутри динамической группы),
     * поэтому на помощь приходит нативный change
     * @type {EventEmitter<any>}
     */
    @Output() change = new EventEmitter<any>();

    @ViewChild('select') select: ElementRef;

    nativeSelect: HTMLSelectElement;
    jQuerySelect;

    private nativeChangeEvent = new Event('change');

    /**
     * Стандартные методы .blur() и .focus() не срабатывают на нативном селекте,
     * поэтому будем тригерить события руками
     * @type {Event}
     */
    private customFocusEvent = new Event('focus');
    private customBlurEvent = new Event('blur');

    @HostBinding('class') private cssClass = 'form-select';
    @HostBinding(`class.form-select--fetching`) private isFetching: boolean;
    @HostBinding('class.form-select--small') @Input() small = false;

    ngOnInit() {
        super.ngOnInit();
        if (this.fbControl.value === null) {
            this.fbControl.patchValue('');
        }
        this.nativeSelect = this.select.nativeElement;
        this.jQuerySelect = $(this.nativeSelect);
    }

    ngAfterViewInit() {
        this.initSelect();
    }

    ngOnChanges() {
        /**
         * Если селект ждет загрузки данных, то показывает прелоадер пока данных нет и раздизейбливается
         * только когда данные пришли
         */
        if (this.showLoading) {
            if (!this.values) {
                this.fbControl.disable();
                this.isFetching = true;
            } else {
                this.isFetching = false;
                if (this.values && this.values.length) {
                    this.fbControl.enable();
                }
            }
        }



        /**
         * Если мы хотим по дефолту выбирать единственное значение,
         * то делаем это только если в селекте до сих пор нет выбранного значения, чтобы не перезатирать его,
         * ибо оставлять в селекте значение, когда такого варианта нет, может быть полезным.
         */
        if (this.selectIfSingleValue && this.values && this.values.length === 1) {
            const value = this.fbControl.value;
            if (!value) {
                FormHelperService.updateWithTouch(this.fbControl, this.values[0].id);
            }
        }

        if (this.checkValueExistence) {
            this.doCheckValueExistence();
        }

        this.refreshSelectView();
    }

    private initSelect() {
        const cssClass = this.cssClass;

        this.jQuerySelect.select2({
            minimumResultsForSearch: this.isSearch ? 0 : Infinity,
            dropdownAutoWidth: this.dropdownAutoWidth,
            templateSelection: (state) => {
                /* Оборачиваем disabled пункт, являющийся заменой placeholder в класс, чтобы стилизовать */
                if (state.element && state.element.hasAttribute('data-placeholder')) {
                    return $('<span class="select2-result-placeholder">' + state.text + '</span>');
                } else {
                    return state.text;
                }
            },
            /**
             * Показываем выпадашку рядом с нативным элементом, чтобы соблюсти z-index наложение.
             * Если оставить по дефолту в body, то есть баг наложения, когда выпадашку ставим под шапку, шапку под модалку,
             * то выпадашка в модалке не видна, так как она физически не в модалке, а в другом месте
             * Обязательно jQuery объект
             * https://select2.org/dropdown#dropdown-placement
             */
            dropdownParent: $(this.nativeSelect.parentElement),
        }).data('select2').$container.addClass(cssClass); // Добавляем классы

        this.setEventsHandlers();
    }

    /**
     * Обновить отображение select2
     */
    private refreshSelectView() {
        if (!this.jQuerySelect) {
            return;
        }
        /**
         * Воспользуемся Scoped версией change события, чтобы не шуметь другим возможным слушателям change.
         * Селект2 воспринимает только jQuery версию события, CustomEvent отчего-то не прокатывает.
         * https://select2.org/programmatic-control/events#limiting-the-scope-of-the-change-event
         */
        this.jQuerySelect.trigger('change.select2');
    }

    /**
     * Проверить наличие выбранного значения среди доступных и если окажется, что выбранного ранее
     * значения больше нет среди доступных, то обнуляем выбор селекта.
     * Это полезно, когда при редактировании нужно сбросить селект, если сохраненного ранее варианта больше нет.
     */
    private doCheckValueExistence() {
        const formValue = this.fbControl.value;
        const isValueExists = this.values &&
            Boolean(this.values.find(v => Number(v.id) === Number(formValue) || String(v.id) === String(formValue)));

        if (!isValueExists) {
            /**
             * Не нашел лучшего способа для обновления формы следующим event loop
             */
            window.requestAnimationFrame(() => {
                this.fbControl.patchValue('');
            });
        }
    }

    private setEventsHandlers() {
        /**
         * После выбора значения плагином нужно ручками вызвать change на элементе,
         * чтобы обработчики ангуляра подхватили новое значение, словно оно изменилось нативно
         */
        this.jQuerySelect.on('select2:select', (e) => {
            this.nativeSelect.dispatchEvent(this.nativeChangeEvent);
        });

        /**
         * Фокусируем и блюрим нативный селект при открытии и закрытии select2
         */
        this.jQuerySelect.on('select2:open', () => {
            // Ручками вызываем customFocus на селекте, чтобы событие поймали Angular директивы
            this.nativeSelect.dispatchEvent(this.customFocusEvent);
        });

        this.jQuerySelect.on('select2:close', () => {
            // Ручками вызываем customBlur на селекте
            this.nativeSelect.dispatchEvent(this.customBlurEvent);
        });

        /**
         * В тот момент, когда в форме оказалось новое значение, нужно обновить визуальный вид для select2,
         * чтобы пользователю отобразилось действительное значение.
         *
         * По хорошему, обновлять нужно только когда значение в форме поменялось без прямых действий пользователя
         * на этот селект (например, patchValue), но у нас нет возможности определить это, поэтому будем
         * обновлять и когда значение пришло в форму вне компонента и когда внутри компонента через действия пользователя.
         */
        this.subs.push(
            this.fbControl.valueChanges.pipe(
                distinctUntilChanged(),
                debounceTime(20),
            ).subscribe(value => {
                if (this.fbControl.value === null || this.fbControl.value === '') {
                    this.jQuerySelect.val('');
                }
                this.refreshSelectView();
            })
        );
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (!this.jQuerySelect) {
            return;
        }
        this.jQuerySelect.off('select2:select');
        this.jQuerySelect.off('select2:open');
        this.jQuerySelect.off('select2:close');
        this.jQuerySelect.unbind('select2:select');
        this.jQuerySelect.unbind('select2:open');
        this.jQuerySelect.unbind('select2:close');
        this.jQuerySelect.select2('destroy');
    }
}
